import { Injectable } from '@angular/core';
import BlocTematicDTO from '@data/models/blocks/block-dto';
import ContentDTO from '@data/models/content/content-dto';

@Injectable({
  providedIn: 'root'
})
export class ContentSortToolService {
  constructor() {}

  /**
   * Recibe como parámetro un array de ContentDTO ya ordenado, y que puede contener el campo orden informado
   * lo que hace la función es colocar los contenido con el campo orden en la posición que se indica dicho campo
   *
   * @param arr: ContentDTO[]
   * @returns ContentDTO[]
   */
  public sortAcordingToOrderNumber(arr: ContentDTO[]): ContentDTO[] {
    const arrWithOrder = arr.filter((c) => c.ordre || c.ordre === 0).sort((a, b) => a.ordre - b.ordre);
    const arrWithoutOrder = arr.filter((c) => !c.ordre && c.ordre !== 0);
    const result = [...arrWithoutOrder];
    arrWithOrder.forEach((c) => {
      result.splice(c.ordre, 0, c);
    });
    return result;
  }

  public changeOrdersInContentArray(
    arr: ContentDTO[] | BlocTematicDTO[],
    c: ContentDTO | BlocTematicDTO,
    newIndex: number,
    removingContent = false
  ): { newArr: ContentDTO[] | BlocTematicDTO[]; contentsWithChanges: ContentDTO[] | BlocTematicDTO[] } {
    const pos = arr.indexOf(c);
    if (pos === -1) {
      return { newArr: arr, contentsWithChanges: [] };
    }
    c.ordre = newIndex;
    const newArray = [...arr];
    const contentsWithChanges = [];
    newArray.splice(pos, 1);
    if (!removingContent) {
      contentsWithChanges.push(c);
      newArray.splice(newIndex, 0, c);
    }
    const startIndex = Math.min(pos, newIndex);
    const endIndex = Math.max(pos, newIndex);
    for (let i = startIndex; i <= endIndex; i++) {
      if (newArray?.length && (newArray[i]?.ordre || newArray[i]?.ordre === 0) && i !== newIndex) {
        if (newIndex < pos) {
          newArray[i].ordre = newArray[i].ordre + 1;
          contentsWithChanges.push(newArray[i]);
        } else if (newIndex > pos) {
          newArray[i].ordre = newArray[i].ordre - 1;
          contentsWithChanges.push(newArray[i]);
        }
      }
    }
    return { newArr: newArray, contentsWithChanges };
  }

  public sortByTitolAndVersion = (a: ContentDTO, b: ContentDTO) => {
    //Primero ordenar por titulo alfanumericamente (ascendente)
    const titolSort = this.titolSort(
      this.transformAlphaNumericStringToSortableString(a?.titol),
      this.transformAlphaNumericStringToSortableString(b?.titol)
    );
    if (titolSort === 1) {
      return 1;
    } else if (titolSort === -1) {
      return -1;
    }
    // Luego ordenar por versión
    if (a.versio < b.versio) {
      return -1;
    } else if (a.versio > b.versio) {
      return 1;
    }
    //Por último estado
    if (a.estat < b.estat) {
      return -1;
    } else if (a.estat > b.estat) {
      return 1;
    } else {
      return 0;
    }
  };

  /**
   * Función que recibe un string que puede contener una máscara del tipo «XXX.XXX.XXX Título».
   * Podrá haber n números separados entre ellos por puntos (hasta un máxio de tres número entre cada punto)
   *
   * @param str
   * @returns string to use in sort function
   */
  public transformAlphaNumericStringToSortableString(str: string): string {
    // Expresión regular para encontrar números separados por puntos y un título opcional
    const regex = /^(\d+)\.?(\d+)?\.?(\d+)?(\s?.+)?$/;

    // Utilizamos match() para buscar números y el título en el input
    const match = str ? str.match(regex) : false;

    if (match) {
      // Capturamos los números y el título
      const num1 = match[1] || '';
      const num2 = match[2] || '';
      const num3 = match[3] || '';
      const titulo = match[4] || '';

      // Concatenamos los números con ceros entre ellos si es necesario
      const numeroFinal = num1.padStart(1, '0') + num2.padStart(3, '0') + num3.padStart(3, '0');

      // Concatenamos el número final con el título
      const resultado = numeroFinal + ' ' + titulo;
      return resultado;
    } else {
      // Si no se encuentra ningún número en el formato, devolvemos el input original
      return str;
    }
  }

  /**
   * Transforma un string en formato fecha a timestamp si es válida
   *
   * @param str fecha en formato DD/MM/YYYY HH:mm:ss
   * @returns timestamp o 0 si la fecha no es válida
   */
  public transformStringDateToSortableNumber(str: string) {
    str = str ? str : '';
    // Dividir la cadena en partes para obtener la fecha y la hora
    const partes = str.split(' ');
    if (partes.length !== 2) {
      return 0; // Si no hay dos partes, la cadena no es válida
    }
    const fechaParte = partes[0];
    const horaParte = partes[1];

    const [dia, mes, año] = fechaParte.split('/').map(Number);
    const [hora, minuto, segundo] = horaParte.split(':').map(Number);
    // Comprobar si la fecha es válida
    if (
      isNaN(año) ||
      isNaN(mes) ||
      isNaN(dia) ||
      isNaN(hora) ||
      isNaN(minuto) ||
      isNaN(segundo) ||
      mes < 1 ||
      mes > 12 ||
      dia < 1 ||
      dia > 31 ||
      hora < 0 ||
      hora > 23 ||
      minuto < 0 ||
      minuto > 59 ||
      segundo < 0 ||
      segundo > 59
    ) {
      return 0;
    }
    const fecha = new Date(año, mes - 1, dia, hora, minuto, segundo);
    if (
      fecha.getDate() !== dia ||
      fecha.getMonth() !== mes - 1 ||
      fecha.getFullYear() !== año ||
      fecha.getHours() !== hora ||
      fecha.getMinutes() !== minuto ||
      fecha.getSeconds() !== segundo
    ) {
      return 0; // La fecha no es válida
    }
    const timestamp = fecha.getTime();

    return timestamp;
  }

  /**
   * Ordenar el título mirando primero si tiene un valor numérico y luego alfabéticamente
   *
   * @param a
   * @param b
   * @returns number
   */
  public titolSort(a: string, b: string): number {
    const valorNumericoA = this.getTitolIndexNumber(a);
    const valorNumericoB = this.getTitolIndexNumber(b);

    if (!isNaN(valorNumericoA) && !isNaN(valorNumericoB)) {
      if (valorNumericoA < valorNumericoB) {
        return -1;
      } else if (valorNumericoA > valorNumericoB) {
        return 1;
      } else {
        return a.localeCompare(b);
      }
    } else if (!isNaN(valorNumericoA)) {
      return -1;
    } else if (!isNaN(valorNumericoB)) {
      return 1;
    } else {
      return a.localeCompare(b);
    }
  }

  /**
   * Recibe como parámetro un array de BlocTematicDTO ya ordenado, y que puede contener el campo orden informado
   * lo que hace la función es colocar los contenido con el campo orden en la posición que se indica dicho campo
   *
   * @param arr: BlocTematicDTO[]
   * @returns BlocTematicDTO[]
   */
  public sortBlocAcordingToOrderNumber(arr: BlocTematicDTO[]): BlocTematicDTO[] {
    const arrWithOrder = arr.filter((c) => c.ordre || c.ordre === 0).sort((a, b) => a.ordre - b.ordre);
    const arrWithoutOrder = arr.filter((c) => !c.ordre && c.ordre !== 0);
    const result = [...arrWithoutOrder];
    arrWithOrder.forEach((c) => {
      result.splice(c.ordre, 0, c);
    });
    return result;
  }

  public sortBlocByNom = (a: BlocTematicDTO, b: BlocTematicDTO) => {
    //Primero ordenar por titulo alfanumericamente (ascendente)
    const titolSort = this.titolSort(
      this.transformAlphaNumericStringToSortableString(a?.nom),
      this.transformAlphaNumericStringToSortableString(b?.nom)
    );
    if (titolSort === 1) {
      return 1;
    } else if (titolSort === -1) {
      return -1;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTitolIndexNumber(str: string): number | any {
    const match = str.match(/^(\d+)/);
    return match ? parseInt(match[0], 10) : NaN;
  }
}
